import { useState } from "react";
import { observer } from "mobx-react-lite";
import { MoreHorizontal, Plus, Trash } from "lucide-react";

import { useCustomParametersStore } from "./custom-parameters-store";
import { ParameterPreview } from "./DraftParameterPreviews";
import { NoParameters } from "./Placeholders/NoParameters";
import { HuggingFaceSettings } from "./HuggingFaceSettings";
import { ParameterBuilderDialog } from "./ParameterBuilderDialog";
import { CustomParametersProps } from "./types";
import { InheritParametersDialog } from "./InheritParametersDialog";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HUGGINGFACE_PROVIDER } from "@/components/ModelParameters/constants";
import { ParameterStore } from "@/components/ModelParameters/store/parameter-store";
import LoadingSpinner from "@/components/LoadingSpinner";
import { ConfirmationModal, ConfirmationModalVariant } from "@/components/ConfirmationModal";

export const CustomParameters = observer(({ provider, isLoading }: CustomParametersProps) => {
  const store = useCustomParametersStore();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const { currentParameter, isEditingParameter, parametersList, isViewing } = store;

  const closeParameterBuilder = () => {
    store.clearParameterSelection();
    setIsOpen(false);
  };

  const shouldConfirmBeforeClose = () => {
    if (isViewing || !currentParameter) {
      return false;
    }

    if (store.isStateDirty && isEditingParameter) {
      return true;
    }

    return !isEditingParameter;
  };

  const attemptCloseParameterBuilder = () => {
    if (shouldConfirmBeforeClose()) {
      setConfirmationModalOpen(true);
      return;
    }

    if (store.hasPreviousState) {
      store.restorePreviousState();
    }

    closeParameterBuilder();
  };

  const handleSaveParameter = () => {
    if (!currentParameter || !store.validateCurrentParameter()) {
      return;
    }

    store.saveParameter(currentParameter);
    closeParameterBuilder();
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setIsOpen(true);
      return;
    }

    attemptCloseParameterBuilder();
  };

  const handleView = (parameter: ParameterStore) => {
    setIsOpen(true);
    store.setCurrentParameter(parameter);
  };

  const handleEdit = (parameter: ParameterStore) => {
    setIsOpen(true);
    store.setCurrentParameter(parameter);
    store.saveCurrentParameterState(parameter);
  };

  const handleDelete = (parameter: ParameterStore) => {
    store.removeParameter(parameter.paramId as string);
    store.setCurrentParameter(null);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <ConfirmationModal
        title="Are you sure you want to leave?"
        description="You have unsaved changes. If you leave, your changes will be lost."
        isOpen={confirmationModalOpen}
        headerVariant={ConfirmationModalVariant.warning}
        setIsOpen={setConfirmationModalOpen}
        useBlocker={false}
        overlayClassName="z-[110]"
        contentClassName="z-[110]"
        leaveButtonText="Discard"
        saveButtonText="Save Parameter"
        handleOnConfirm={() => {
          if (currentParameter && !store.validateCurrentParameter()) {
            setConfirmationModalOpen(false);
            return;
          }

          if (currentParameter && store.isEditingParameter) {
            store.saveParameter(currentParameter);
          }

          if (currentParameter && !store.isEditingParameter) {
            store.saveParameter(currentParameter);
          }

          setConfirmationModalOpen(false);
          closeParameterBuilder();
        }}
        handleLeave={() => {
          store.restorePreviousState();
          setConfirmationModalOpen(false);
          closeParameterBuilder();
        }}
      />
      {isLoading && (
        <div className="absolute left-1/2 top-1/2 z-10 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center">
          <div className="absolute inset-0 bg-gray-50/30 backdrop-blur-sm" />
          <LoadingSpinner className="h-4 w-4" />
        </div>
      )}
      <div className="relative col-span-2 mb-6 grid">
        <div className="grid max-h-[580px] grid-cols-2 gap-5 overflow-y-auto pt-4 transition-[max-height] duration-300 ease-in-out max-h-952:max-h-[450px] max-h-800:max-h-[350px]">
          {parametersList.map(parameter => (
            <ParameterPreview
              key={parameter.paramId}
              parameter={parameter}
              isViewing={isViewing}
              onView={() => handleView(parameter)}
              onEdit={() => handleEdit(parameter)}
              onDelete={() => handleDelete(parameter)}
            />
          ))}
        </div>
        <NoParameters />
      </div>
      <div className="col-span-2 flex items-center justify-between">
        <div className="flex w-full items-center justify-between">
          <div className="flex gap-3">
            <Button
              type="button"
              disabled={isViewing}
              variant="outline"
              onClick={() => {
                store.clearParameterSelection();
                setIsOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add a new parameter
            </Button>
            <InheritParametersDialog />
            {!isViewing && parametersList.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger
                  hideChevron
                  type="button"
                  className="inline-flex h-9 w-9 rotate-90 items-center justify-center rounded-md border-0 bg-transparent p-0 shadow-none hover:bg-accent"
                  aria-label="Delete all parameters"
                  title="Delete all parameters"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    className="w-full cursor-pointer hover:!bg-red-50"
                    onClick={() => store.removeAllParameters()}
                  >
                    <div className="flex items-center gap-2 text-red-500">
                      <Trash className="h-4 w-4" />
                      Delete all
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {provider === HUGGINGFACE_PROVIDER && <HuggingFaceSettings />}
        </div>
      </div>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          overlayClassName="z-[100]"
          className="z-[100] flex h-[864px] max-h-[864px] max-w-[1200px] flex-col overflow-hidden"
        >
          <ParameterBuilderDialog
            onRequestClose={attemptCloseParameterBuilder}
            onSave={handleSaveParameter}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
});
